import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useLanguage} from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SupportPopup from './SupportPopup';

const {FiGlobe, FiChevronDown, FiCheck, FiHeart} = FiIcons;

const Header = () => {
  const {t, currentLang, changeLanguage} = useLanguage();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isSupportPopupOpen, setIsSupportPopupOpen] = useState(false);

  const languages = [
    {code: 'zh', name: '中文', shortName: '中'},
    {code: 'en', name: 'English', shortName: 'EN'},
    {code: 'ja', name: '日本語', shortName: '日'},
    {code: 'es', name: 'Español', shortName: 'ES'}
  ];

  const currentLanguage = languages.find(lang => lang.code === currentLang);

  // 🆕 修改語言切換處理函數
  const handleLanguageChange = (code) => {
    setIsLangMenuOpen(false);
    
    // 使用 changeLanguage 會導航到靜態 HTML
    changeLanguage(code);
  };

  return (
    <motion.header
      className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 text-white shadow-lg"
      initial={{opacity: 0, y: -20}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.5}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4 sm:py-5">
          {/* Logo 和標題 */}
          <motion.div
            className="flex items-center gap-3"
            initial={{scale: 0.9, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            transition={{delay: 0.2, type: "spring", stiffness: 200}}
          >
            <motion.div
              className="relative w-10 h-10 sm:w-12 sm:h-12"
              whileHover={{scale: 1.1, rotate: 360}}
              whileTap={{scale: 0.95}}
              transition={{duration: 0.6}}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-blue-400/30 to-cyan-400/20 rounded-xl backdrop-blur-sm border border-white/20"
                animate={{rotate: 360}}
                transition={{duration: 20, repeat: Infinity, ease: "linear"}}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-6 h-6 sm:w-7 sm:h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3H17L21 7V21H3V3H7Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" fillOpacity="0.2" />
                  <motion.path
                    d="M12 8L16 12L12 16M8 12H16"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{pathLength: 0, opacity: 0}}
                    animate={{pathLength: 1, opacity: 1}}
                    transition={{duration: 1.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut"}}
                  />
                </svg>
              </div>
            </motion.div>

            <div className="flex flex-col">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight tracking-tight">
                Convert Master
              </h1>
              <p className="text-xs sm:text-sm text-white/80 hidden sm:block font-medium">
                {t('title').split(' ').slice(-2).join(' ')}
              </p>
            </div>
          </motion.div>

          {/* 右側控制區 */}
          <div className="flex items-center gap-3">
            {/* 支持我們按鈕 */}
            <motion.button
              className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 shadow-lg transition-all duration-300"
              onClick={() => setIsSupportPopupOpen(true)}
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              initial={{opacity: 0, x: 20}}
              animate={{opacity: 1, x: 0}}
              transition={{delay: 0.3}}
            >
              <motion.div
                animate={{scale: [1, 1.2, 1]}}
                transition={{duration: 1.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut"}}
              >
                <SafeIcon icon={FiHeart} className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </motion.div>
              <span className="text-sm sm:text-base font-semibold hidden sm:inline">
                {t('supportUs')}
              </span>
            </motion.button>

            {/* 語言選擇器 - 桌面版 */}
            <motion.div
              className="hidden lg:block relative"
              initial={{opacity: 0, x: 20}}
              animate={{opacity: 1, x: 0}}
              transition={{delay: 0.4}}
            >
              <motion.button
                className="flex items-center gap-2 bg-white/10 backdrop-blur-xl rounded-xl px-4 py-2.5 border border-white/20 shadow-lg hover:bg-white/15 transition-colors min-w-[140px]"
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                whileTap={{scale: 0.95}}
              >
                <SafeIcon icon={FiGlobe} className="text-white text-lg" />
                <span className="text-sm font-medium flex-1">
                  {currentLanguage?.name}
                </span>
                <motion.div
                  animate={{rotate: isLangMenuOpen ? 180 : 0}}
                  transition={{duration: 0.3}}
                >
                  <SafeIcon icon={FiChevronDown} className="text-white text-sm" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {isLangMenuOpen && (
                  <>
                    <motion.div
                      className="fixed inset-0 z-40"
                      initial={{opacity: 0}}
                      animate={{opacity: 1}}
                      exit={{opacity: 0}}
                      onClick={() => setIsLangMenuOpen(false)}
                    />
                    <motion.div
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                      initial={{opacity: 0, y: -10, scale: 0.95}}
                      animate={{opacity: 1, y: 0, scale: 1}}
                      exit={{opacity: 0, y: -10, scale: 0.95}}
                      transition={{duration: 0.2}}
                    >
                      {languages.map((lang, index) => (
                        <motion.button
                          key={lang.code}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            currentLang === lang.code
                              ? 'bg-slate-50 text-slate-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => handleLanguageChange(lang.code)}
                          initial={{opacity: 0, x: -10}}
                          animate={{opacity: 1, x: 0}}
                          transition={{delay: index * 0.05}}
                          whileTap={{scale: 0.98}}
                        >
                          <div className="flex-1">
                            <div className="font-medium">{lang.name}</div>
                          </div>
                          {currentLang === lang.code && (
                            <motion.div
                              initial={{scale: 0}}
                              animate={{scale: 1}}
                              transition={{type: "spring", stiffness: 500}}
                            >
                              <SafeIcon icon={FiCheck} className="w-5 h-5 text-slate-700" />
                            </motion.div>
                          )}
                        </motion.button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </motion.div>

            {/* 語言選擇器 - 手機版 */}
            <motion.div
              className="lg:hidden relative"
              initial={{opacity: 0, x: 20}}
              animate={{opacity: 1, x: 0}}
              transition={{delay: 0.4}}
            >
              <motion.button
                className="flex items-center gap-2 bg-white/10 backdrop-blur-xl rounded-xl px-3 py-2 border border-white/20 shadow-lg hover:bg-white/15 transition-colors"
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                whileTap={{scale: 0.95}}
              >
                <span className="text-sm font-medium">{currentLanguage?.shortName}</span>
                <motion.div
                  animate={{rotate: isLangMenuOpen ? 180 : 0}}
                  transition={{duration: 0.3}}
                >
                  <SafeIcon icon={FiChevronDown} className="text-white text-sm" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {isLangMenuOpen && (
                  <>
                    <motion.div
                      className="fixed inset-0 z-40"
                      initial={{opacity: 0}}
                      animate={{opacity: 1}}
                      exit={{opacity: 0}}
                      onClick={() => setIsLangMenuOpen(false)}
                    />
                    <motion.div
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                      initial={{opacity: 0, y: -10, scale: 0.95}}
                      animate={{opacity: 1, y: 0, scale: 1}}
                      exit={{opacity: 0, y: -10, scale: 0.95}}
                      transition={{duration: 0.2}}
                    >
                      {languages.map((lang, index) => (
                        <motion.button
                          key={lang.code}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            currentLang === lang.code
                              ? 'bg-slate-50 text-slate-900'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => handleLanguageChange(lang.code)}
                          initial={{opacity: 0, x: -10}}
                          animate={{opacity: 1, x: 0}}
                          transition={{delay: index * 0.05}}
                          whileTap={{scale: 0.98}}
                        >
                          <div className="flex-1">
                            <div className="font-medium">{lang.name}</div>
                          </div>
                          {currentLang === lang.code && (
                            <motion.div
                              className="w-2 h-2 bg-slate-700 rounded-full"
                              initial={{scale: 0}}
                              animate={{scale: 1}}
                              transition={{type: "spring", stiffness: 500}}
                            />
                          )}
                        </motion.button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 裝飾性波浪 */}
      <div className="relative h-3 overflow-hidden">
        <svg className="absolute bottom-0 w-full h-3" viewBox="0 0 1200 20" preserveAspectRatio="none">
          <motion.path
            d="M0,10 Q300,0 600,10 T1200,10 L1200,20 L0,20 Z"
            fill="white"
            initial={{d: "M0,10 Q300,0 600,10 T1200,10 L1200,20 L0,20 Z"}}
            animate={{
              d: [
                "M0,10 Q300,0 600,10 T1200,10 L1200,20 L0,20 Z",
                "M0,10 Q300,20 600,10 T1200,10 L1200,20 L0,20 Z",
                "M0,10 Q300,0 600,10 T1200,10 L1200,20 L0,20 Z"
              ]
            }}
            transition={{duration: 10, repeat: Infinity, ease: "easeInOut"}}
          />
        </svg>
      </div>

      <SupportPopup
        isOpen={isSupportPopupOpen}
        onClose={() => setIsSupportPopupOpen(false)}
      />
    </motion.header>
  );
};

export default Header;